import { ReactNode, useState } from "react";

import InfoIcon from "src/assets/icons/SsqTitleIcon/info.svg?react";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import MediumForklift from "src/assets/icons/forklift-medium.svg?react";
import { useTranslation } from "react-i18next";
import { Button, Stack, Typography } from "@mui/material";
import { ButtonTabs } from "@foodpilot/foods";
import { useNavigate, useLocation } from "react-router";
import { GenericBox } from "src/components/GenericBox";
import CreateOutlined from "@mui/icons-material/CreateOutlined";

import { Ingredient } from "src/api/client-api/ingredients/useGetOneIngredient";
import { InformationSummary } from "src/components/Summary/IngredientSummary/InformationSummary";
import { SupplierSummary } from "src/components/Summary/IngredientSummary/SupplierSummary";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";
import { useIngredientContext } from "../useIngredientContext";
import { ExtensionPoint, EXTENSION_POINTS } from "src/components/PuckExtensions";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";
import { IngredientInformationsV2 } from "./IngredientInformationsV2";

type ModifyButtonProps = {
    ingredient: Ingredient;
    pageNumber: number;
};
const ModifyButton = (props: ModifyButtonProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const { selectedCompany } = useFoodpilotContext();
    const { ingredient, pageNumber } = props;

    if (selectedCompany === null) return;

    const modifyText = t("modifier");
    if (ingredient.canModify === false) {
        return null;
    }

    return (
        <Button
            variant="primary"
            startIcon={<CreateOutlined />}
            sx={{ marginRight: "24px", borderRadius: "44px" }}
            onClick={() => {
                const currentUrl = location.pathname + location.search;
                const editUrl = URLHandler.ingredient.edit(selectedCompany.id, ingredient.id, pageNumber);
                navigate(`${editUrl}?returnUrl=${encodeURIComponent(currentUrl)}`);
            }}
        >
            {modifyText}
        </Button>
    );
};

type IngredientInformationsProps = unknown;

type Page = {
    title: string;
    icon?: ReactNode;
    content: ReactNode | ReactNode[];
};

export const Informations = (_props: IngredientInformationsProps) => {
    const isProductUxV2 = useIsProductUxV2();

    return isProductUxV2 ? <IngredientInformationsV2 /> : <IngredientInformationsV1 />;
};

const IngredientInformationsV1 = () => {
    const { t } = useTranslation();
    const { isUserOfTypeSupplier } = useFoodpilotContext();
    const { ingredient, showIngredientSuppliers } = useIngredientContext();
    const [currentPage, setCurrentPage] = useState(0);

    const isSupplier = isUserOfTypeSupplier();
    const title = isSupplier ? t("informations_sur_l_ingredient") : t("informations");

    if (ingredient === undefined) return;

    const allPages: Page[] = [
        {
            title: t("general_information"),
            icon: <InfoIcon />,
            content: (
                <GenericBox
                    headerIcon={<InfoOutlined />}
                    headerText={t("general_information")}
                    controls={<ModifyButton pageNumber={0} ingredient={ingredient} />}
                >
                    <InformationSummary ingredient={ingredient} />
                </GenericBox>
            ),
        },
    ];

    if (showIngredientSuppliers) {
        allPages.push({
            title: t("suppliers"),
            icon: <MediumForklift />,
            content: (
                <GenericBox
                    headerIcon={<MediumForklift />}
                    headerText={t("suppliers")}
                    controls={<ModifyButton pageNumber={1} ingredient={ingredient} />}
                >
                    <SupplierSummary suppliers={ingredient.suppliers} />
                </GenericBox>
            ),
        });
    }

    const choices = allPages.map((p, index) => (
        <Stack key={index} direction="row" alignItems="center" gap="8px">
            {p.icon}
            {p.title}
        </Stack>
    ));

    return (
        <Stack gap="28px">
            <ExtensionPoint extensionPoint={EXTENSION_POINTS.INGREDIENT_DETAIL_INFORMATION_HEADER} />
            <Typography variant="h2">{title}</Typography>
            <ButtonTabs choices={choices} onClick={(choiceIndex) => setCurrentPage(choiceIndex)} />
            {allPages[currentPage].content}
            <ExtensionPoint extensionPoint={EXTENSION_POINTS.INGREDIENT_DETAIL_INFORMATION_FOOTER} />
        </Stack>
    );
};
